import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import type {
  CreateCustomTaxRateInput,
  CreateCustomTaxRateResponse,
  SalesTaxTargetAccountType,
  TaxLocalitiesResponse,
  UpsertTaxSettingsInput,
} from '@releve/shared';
import { authedGet, authedPost, authedPut } from '@/lib/api';
import { toast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

type OrganizationTaxSettingsRow = Tables<'organization_tax_settings'>;
type TaxRateRow = Tables<'tax_rates'>;

/**
 * Hook to fetch distinct active jurisdiction codes from tax_rate_templates.
 * Used to populate the jurisdiction dropdown dynamically. Only state /
 * country rows count (`locality_code IS NULL`): a town's local rate is not a
 * jurisdiction of its own (20261028100300).
 *
 * Still a direct read (no api endpoint lists jurisdictions yet; the global
 * templates are SELECT-able by every authenticated user).
 */
export function useAvailableJurisdictions() {
  return useQuery({
    queryKey: ['available-jurisdictions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tax_rate_templates')
        .select('jurisdiction_code')
        .is('effective_to', null)
        .is('locality_code', null);
      if (error) throw error;
      const codes = [...new Set(data.map(r => r.jurisdiction_code))].sort();
      return codes;
    },
    staleTime: 1000 * 60 * 60, // 1hr — jurisdictions rarely change
  });
}

/**
 * The towns a jurisdiction has local rates for (GET /api/v1/tax-settings/localities):
 * Vermont's local option tax towns and the cities that collect their own tax.
 * Empty for a jurisdiction without local rates.
 */
export function useTaxLocalities(jurisdiction?: string | null) {
  return useQuery({
    queryKey: ['tax-localities', jurisdiction],
    queryFn: async () => {
      const { localities } = await authedGet<TaxLocalitiesResponse>(
        `/api/v1/tax-settings/localities?jurisdiction=${encodeURIComponent(jurisdiction!)}`,
      );
      return localities;
    },
    enabled: !!jurisdiction,
    staleTime: 60 * 60 * 1000, // 1hr — templates change by migration / rate sync only
  });
}

/**
 * Hook to fetch the full organization_tax_settings row for the settings page.
 */
export function useFullTaxSettings(organizationId?: string) {
  return useQuery({
    queryKey: ['organization-tax-settings-full', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from('organization_tax_settings')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;
      return data as OrganizationTaxSettingsRow | null;
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 min — tax settings change infrequently
  });
}

/**
 * An org rate row plus the two lineage ids `rateInForce` (@releve/shared)
 * walks: the `supersedes_template_id` of the row's state template and of its
 * local template (NULL for a custom rate, a first version, or before the
 * lineage column exists). docs/TAX.md "Rate changes".
 */
export type TaxRateWithLineage = TaxRateRow & {
  supersedes_template_id: string | null;
  local_supersedes_template_id: string | null;
};

type TaxRateLineageEmbed = { supersedes_template_id: string | null } | null;
type TaxRateRowWithEmbeds = TaxRateRow & {
  template?: TaxRateLineageEmbed;
  local_template?: TaxRateLineageEmbed;
};

/** The rates select with the two template embeds that carry the lineage. */
const TAX_RATES_LINEAGE_SELECT =
  '*, template:tax_rate_templates!tax_rates_template_id_fkey(supersedes_template_id), ' +
  'local_template:tax_rate_templates!tax_rates_local_template_id_fkey(supersedes_template_id)';

/** Postgres "undefined_column": the lineage column is not deployed yet. */
const UNDEFINED_COLUMN = '42703';

/** Flatten the embeds into the two lineage ids. */
export function withTaxRateLineage(row: TaxRateRowWithEmbeds): TaxRateWithLineage {
  const { template, local_template, ...rate } = row;
  return {
    ...rate,
    supersedes_template_id: template?.supersedes_template_id ?? null,
    local_supersedes_template_id: local_template?.supersedes_template_id ?? null,
  };
}

/**
 * Hook to fetch tax_rates rows for the organization — every row, expired and
 * future-dated included (the pickers window-filter them, and the lineage walk
 * needs them all), each with its lineage ids.
 *
 * Still a direct read (no api endpoint lists an org's rates yet). The web
 * ships before the migration that adds `tax_rate_templates.
 * supersedes_template_id`, so an "undefined column" error falls back to the
 * plain select with no lineage: an account default then applies only while
 * its own rate is in force.
 */
export function useOrganizationTaxRates(organizationId?: string) {
  return useQuery({
    queryKey: ['organization-tax-rates', organizationId],
    queryFn: async (): Promise<TaxRateWithLineage[]> => {
      if (!organizationId) return [];

      const read = (columns: string) =>
        supabase
          .from('tax_rates')
          .select(columns)
          .eq('organization_id', organizationId)
          .order('sort_order', { ascending: true });

      let { data, error } = await read(TAX_RATES_LINEAGE_SELECT);
      if (error?.code === UNDEFINED_COLUMN) ({ data, error } = await read('*'));
      if (error) throw error;
      return ((data ?? []) as unknown as TaxRateRowWithEmbeds[]).map(withTaxRateLineage);
    },
    enabled: !!organizationId,
    // Reference data; every tax-rate write invalidates this key.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation hook to upsert organization_tax_settings through the backend
 * (PUT /api/v1/tax-settings/orgs/:orgId/settings — org-admin only, 403
 * `admin_required` otherwise). The first save INSERTs, which fires
 * trg_setup_tax_accounts (automatic GL account + tax rate creation); later
 * saves UPDATE the org's single row.
 */
export function useUpsertTaxSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      data,
    }: {
      organizationId: string;
      data: UpsertTaxSettingsInput;
    }) => {
      await authedPut<OrganizationTaxSettingsRow>(
        `/api/v1/tax-settings/orgs/${organizationId}/settings`,
        data,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organization-tax-settings-full', variables.organizationId] });
      // A jurisdiction or town change re-syncs the org's rates (and may add
      // tax accounts) server-side.
      queryClient.invalidateQueries({ queryKey: ['organization-tax-rates', variables.organizationId] });
    },
  });
}

/**
 * Mutation hook to create a custom (non-system) tax rate — the backend runs
 * create_custom_tax_rate (POST /api/v1/tax-settings/orgs/:orgId/rates).
 */
export function useCreateCustomTaxRate(organizationId?: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (params: {
      name: string;
      rate: number;
      direction: 'OUTPUT' | 'INPUT';
      taxType?: string;
      isRecoverable?: boolean;
      /** SALES_TAX OUTPUT only: which payable account the rate posts to. */
      targetAccountType?: SalesTaxTargetAccountType;
    }) => {
      if (!organizationId) throw new Error('No organization selected');

      const body: CreateCustomTaxRateInput = {
        name: params.name,
        rate: params.rate,
        direction: params.direction,
        tax_type: (params.taxType ?? 'VAT') as CreateCustomTaxRateInput['tax_type'],
        is_recoverable: params.isRecoverable ?? false,
        ...(params.targetAccountType ? { target_account_type: params.targetAccountType } : {}),
      };
      const { id } = await authedPost<CreateCustomTaxRateResponse>(
        `/api/v1/tax-settings/orgs/${organizationId}/rates`,
        body,
      );
      return id; // the new rate UUID
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-tax-rates', organizationId] });
      toast.success(t('tax.settings.customRates.created'));
    },
    onError: (error: Error) => {
      // Surface the RPC error message (e.g., duplicate rate)
      toast.error(error.message || t('tax.settings.customRates.createFailed'));
    },
  });
}

/**
 * Mutation hook to deactivate a custom tax rate — the backend pins the rate to
 * the org, then runs deactivate_custom_tax_rate
 * (POST /api/v1/tax-settings/orgs/:orgId/rates/:rateId/deactivate).
 */
export function useDeactivateCustomTaxRate(organizationId?: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (rateId: string) => {
      if (!organizationId) throw new Error('No organization selected');
      await authedPost<{ id: string }>(
        `/api/v1/tax-settings/orgs/${organizationId}/rates/${rateId}/deactivate`,
        {},
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-tax-rates', organizationId] });
      toast.success(t('tax.settings.customRates.deactivated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('tax.settings.customRates.deactivateFailed'));
    },
  });
}
